/**
 * Audio analysis frames for the Shader node — pure helpers shared by the host's
 * audio source and the tests. The frame shape is Web Audio's own (see contract).
 */

import { AUDIO_BIN_COUNT, AUDIO_SILENCE_DB } from './contract'
import type { ShaderAudioFrame } from './contract'

/** Frames reach the sandbox about 30 times a second: smooth enough to dance to, cheap to post. */
export const AUDIO_FRAME_INTERVAL_MS = 1000 / 30

export function createSilentAudioFrame(): ShaderAudioFrame {
  return { fft: new Float32Array(AUDIO_BIN_COUNT).fill(AUDIO_SILENCE_DB), waveform: new Float32Array(AUDIO_BIN_COUNT), amplitude: 0 }
}

/** Root mean square of a -1..1 waveform, clamped to 0..1. Non-finite samples count as silence. */
export function computeRms(waveform: ArrayLike<number>): number {
  if (waveform.length === 0) return 0
  let sum = 0
  for (let i = 0; i < waveform.length; i++) {
    const v = waveform[i]
    if (Number.isFinite(v)) sum += v * v
  }
  return Math.min(1, Math.sqrt(sum / waveform.length))
}

/**
 * `getFloatFrequencyData` reports silent bins as `-Infinity`; the contract's floor is
 * `AUDIO_SILENCE_DB`. Clamps in place and returns the same array.
 */
export function clampDecibels(fft: Float32Array): Float32Array {
  for (let i = 0; i < fft.length; i++) {
    const v = fft[i]
    fft[i] = Number.isNaN(v) || v < AUDIO_SILENCE_DB ? AUDIO_SILENCE_DB : v > 0 ? 0 : v
  }
  return fft
}

/** A frame from raw analyser output. The arrays are copied, so the analyser's buffers can be reused. */
export function buildAudioFrame(fft: Float32Array, waveform: Float32Array): ShaderAudioFrame {
  const frameFft = clampDecibels(Float32Array.from(fft))
  const frameWaveform = Float32Array.from(waveform, (v) => (Number.isFinite(v) ? Math.max(-1, Math.min(1, v)) : 0))
  return { fft: frameFft, waveform: frameWaveform, amplitude: computeRms(frameWaveform) }
}

/** An FFT bin in decibels as the 0..1 magnitude shaders read: `(dB + 140) / 140`, clamped. */
export function normalizeDecibels(db: number): number {
  if (!Number.isFinite(db)) return db > 0 ? 1 : 0
  return Math.max(0, Math.min(1, (db - AUDIO_SILENCE_DB) / -AUDIO_SILENCE_DB))
}

/** Emits at most once per interval; the first call always emits. */
export function createFrameThrottle(intervalMs: number = AUDIO_FRAME_INTERVAL_MS): (now: number) => boolean {
  let last = -Infinity
  return (now) => {
    // A little slack so a 60 Hz loop reliably lands on every second frame.
    if (now - last < intervalMs - 4) return false
    last = now
    return true
  }
}
